import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';
import DownloadButton from '../components/DownloadButton';
import useImagePreloader from '../hooks/useImagePreloader';

const GalleryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding: 2rem 0;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  margin: 0 0 3rem 0;
  border-radius: 16px;
  
  @media (max-width: 767px) {
    margin: 0 -2rem 3rem -2rem;
    border-radius: 0;
  }
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
  max-width: 600px;
  margin: 0 auto;
`;

const CategorySection = styled(motion.section)`
  margin-bottom: 4rem;
`;

const CategoryTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 2rem;
  text-align: center;
`;

const CategoryDescription = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.gray};
  margin-bottom: 2rem;
  font-size: 1.1rem;
`;

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
`;

const PhotoItem = styled(motion.div)`
  position: relative;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  }
`;

const PhotoOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.7));
  color: white;
  padding: 1rem;
  transform: translateY(100%);
  transition: transform 0.3s ease;
  
  ${PhotoItem}:hover & {
    transform: translateY(0);
  }
`;

const PhotoCaption = styled.p`
  font-size: 0.9rem;
  margin: 0;
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled(motion.div)`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0,0,0,0.5);
`;

const ModalCaption = styled.div`
  padding: 1rem;
  background: white;
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0,0,0,0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(0,0,0,0.9);
  }
`;

const EmptyCategory = styled.div`
  text-align: center;
  padding: 3rem;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  border-radius: 12px;
  border: 2px dashed #cbd5e0;
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0,0,0,0.7);
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s ease;
  z-index: 1001;
  
  &:hover {
    background: rgba(0,0,0,0.9);
  }
  
  &.prev {
    left: 1rem;
  }
  
  &.next {
    right: 1rem;
  }
`;

const KeyboardHint = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  opacity: 0.8;
`;

// Kids section styled components
const KidsSection = styled.div`
  background: linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%);
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const DropdownContainer = styled.div`
  margin-bottom: 2rem;
`;

const DropdownSelect = styled.select`
  width: 100%;
  padding: 1rem;
  font-size: 1.1rem;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 8px;
  background: white;
  color: ${props => props.theme.colors.primary};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const KidsStoryContent = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-top: 2rem;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
`;

const VideoContainer = styled.div`
  margin: 2rem 0;
  text-align: center;
`;

const VideoPlayer = styled.video`
  width: 100%;
  max-width: 600px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
`;

const Quote = styled.blockquote`
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  border-left: 4px solid ${props => props.theme.colors.secondary};
  padding: 2rem;
  margin: 2rem 0;
  font-style: italic;
  font-size: 1.1rem;
  line-height: 1.8;
  
  footer {
    text-align: right;
    font-weight: bold;
    color: ${props => props.theme.colors.primary};
    margin-top: 1rem;
  }
`;

const KidsStoryTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
`;

const KidsStorySection = styled.div`
  margin-bottom: 2rem;
`;

const NextSectionContainer = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 2rem;
  margin: 3rem auto 2rem auto;
  text-align: center;
  max-width: 600px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
`;

const NextSectionButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }
`;

// Hero Recognition Styled Components
const HeroRecognitionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
  }
`;

const HeroCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
    border-color: #3182ce;
  }
`;

const HeroImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 1rem;
  border: 3px solid ${props => props.theme.colors.primary};
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const HeroName = styled.h3`
  color: #2d3748;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  line-height: 1.3;
`;

const HeroRole = styled.p`
  color: #4a5568;
  font-size: 0.9rem;
  font-style: italic;
  margin: 0;
`;

const CityRecognitionBanner = styled.div`
  background: linear-gradient(135deg, #2c5282 0%, #3182ce 100%);
  color: white;
  border-radius: 12px;
  padding: 2rem;
  margin-top: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 4px 12px rgba(44, 82, 130, 0.3);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
`;

const CityIcon = styled.div`
  font-size: 3rem;
  flex-shrink: 0;
  filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.3));
`;

const CityRecognitionText = styled.div`
  flex: 1;
`;

const CityRecognitionTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: white;
  font-weight: 600;
`;

const CityRecognitionDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
  opacity: 0.95;
`;

// Crew member image mapping
const crewImages = {
  'Ryan Rackman': '/crew/Saratoga Springs Fire & Rescue - Captain - Ryan Rackman.png',
  'Alec M. Olsen': '/crew/Saratoga Springs Fire & Rescue - Firefighter Aemt - Alec M. Olsen.png',
  'Ivan Radcliff': '/crew/Saratoga Springs Fire & Rescue - Engineer - Ivan Radcliff.png',
  'Isaac Ostler': '/crew/Saratoga Springs Fire & Rescue - Firefighter Critical Care Paramedic - Isaac Ostler.png',
  'Eliza Soward': '/crew/Eliza Soward.png',
  'Josh Cluff': '/crew/Saratoga Springs Fire & Rescue - Firefighter EMT - Josh Cluff.png',
  'Seis Wetherell': '/crew/Saratoga Springs Fire & Rescue - Captain - Seis Wetherell.png',
  'David Perry': '/crew/Saratoga Springs Fire & Rescue - Engineer - David Perry.png',
  'Abby Walz': '/crew/Saratoga Springs Fire & Rescue - Paramedic - Abby Walz .png'
};

const Gallery = () => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedKid, setSelectedKid] = useState('');

  const handleKidSelection = (e) => {
    setSelectedKid(e.target.value);
  };

  const kidsStories = useMemo(() => ({
    eliza: {
      name: "Eliza Jane Soward",
      age: "17 years old",
      title: "The Hero Who Saved a Life",
      content: (
        <KidsStoryContent>
          <KidsStoryTitle>The Moment That Changed Everything</KidsStoryTitle>
          <p>
            On Thanksgiving morning 2024, 17-year-old Eliza Jane Soward became a hero in the most 
            unexpected way. When her mother Maggie collapsed from sudden cardiac arrest, Eliza's 
            quick thinking, courage, and life-saving skills turned what could have been a tragedy 
            into a miracle.
          </p>
          
          <Quote>
            "I just knew I had to act. There wasn't time to think - only time to do what I had been trained to do."
            <footer>— Eliza Jane Soward</footer>
          </Quote>

          <KidsStorySection>
            <KidsStoryTitle>🚨 The Emergency Response</KidsStoryTitle>
            <p>
              When Maggie collapsed in the kitchen, Eliza immediately recognized the signs of cardiac 
              arrest. Drawing on her CPR training and natural instincts, she performed life-saving CPR 
              that kept her mother alive until first responders arrived.
            </p>
          </KidsStorySection>

          <KidsStorySection>
            <KidsStoryTitle>Eliza's Testimony</KidsStoryTitle>
            <p>
              In this powerful video testimony, Eliza shares her experience of that life-changing moment 
              and reflects on being used as an instrument in God's hands to save her mother's life.
            </p>
            
            <VideoContainer>
              <VideoPlayer 
                controls 
                preload="metadata"
                poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='400' viewBox='0 0 600 400'%3E%3Crect width='600' height='400' fill='%23f7fafc'/%3E%3Ctext x='300' y='200' text-anchor='middle' fill='%23e53e3e' font-size='24' font-family='Arial'%3E🎥 Eliza's Testimony%3C/text%3E%3C/svg%3E"
              >
                <source src="https://maggies-miracle-media-2025-ssoward.s3.amazonaws.com/video/2025-06-23/Elizas_Testimony_12.1.2024.mov" type="video/mp4" />
                <source src="https://maggies-miracle-media-2025-ssoward.s3.amazonaws.com/video/2025-06-23/Elizas_Testimony_12.1.2024.mov" type="video/quicktime" />
                Your browser does not support the video tag.
              </VideoPlayer>
            </VideoContainer>
          </KidsStorySection>
        </KidsStoryContent>
      )
    },
    anna: {
      name: "Anna Soward",
      age: "12 years old",
      title: "Gaming Buddy's Perspective",
      content: (
        <KidsStoryContent>
          <KidsStoryTitle>🎥 Anna's Interview with Dad</KidsStoryTitle>
          <p>
            In this heartfelt interview, 12-year-old Anna shares her experience of that life-changing 
            Thanksgiving morning and reflects on what makes her mom so special.
          </p>
          
          <VideoContainer>
            <VideoPlayer 
              controls 
              preload="metadata"
              poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='400' viewBox='0 0 600 400'%3E%3Crect width='600' height='400' fill='%23f7fafc'/%3E%3Ctext x='300' y='200' text-anchor='middle' fill='%23e53e3e' font-size='24' font-family='Arial'%3E🎥 Anna's Interview%3C/text%3E%3C/svg%3E"
            >
              <source src="https://maggies-miracle-media-2025-ssoward.s3.amazonaws.com/video/2025-06-28/annas-thougths-maggies-event.mov" type="video/mp4" />
              <source src="https://maggies-miracle-media-2025-ssoward.s3.amazonaws.com/video/2025-06-28/annas-thougths-maggies-event.mov" type="video/quicktime" />
              Your browser does not support the video tag.
            </VideoPlayer>
          </VideoContainer>
        </KidsStoryContent>
      )
    },
    violet: {
      name: "Violet Soward",
      age: "9 years old",
      title: "Little Sister's Perspective",
      content: (
        <KidsStoryContent>
          <KidsStoryTitle>🎥 Violet's Interview with Dad</KidsStoryTitle>
          <p>
            At 9 years old, Violet experienced the scary events from her unique perspective. 
            In this touching interview, she shares her memories and feelings about her mom's miracle.
          </p>
          
          <VideoContainer>
            <VideoPlayer 
              controls 
              preload="metadata"
              poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='400' viewBox='0 0 600 400'%3E%3Crect width='600' height='400' fill='%23f7fafc'/%3E%3Ctext x='300' y='200' text-anchor='middle' fill='%23e53e3e' font-size='24' font-family='Arial'%3E🎥 Violet's Interview%3C/text%3E%3C/svg%3E"
            >
              <source src="https://maggies-miracle-media-2025-ssoward.s3.amazonaws.com/video/2025-06-28/violets-thougths-maggies-event.mov" type="video/mp4" />
              <source src="https://maggies-miracle-media-2025-ssoward.s3.amazonaws.com/video/2025-06-28/violets-thougths-maggies-event.mov" type="video/video/quicktime" />
              Your browser does not support the video tag.
            </VideoPlayer>
          </VideoContainer>
        </KidsStoryContent>
      )
    },
    sophia: {
      name: "Sophia Soward",
      age: "6 years old",
      title: "The Youngest Helper's Heart",
      content: (
        <KidsStoryContent>
          <KidsStoryTitle>🎥 Sophia's Interview with Dad</KidsStoryTitle>
          <p>
            At just 6 years old, Sophia experienced the scary and confusing events of that Thanksgiving 
            morning. In this sweet interview, she shares her memories and feelings about her mom's miracle.
          </p>
          
          <VideoContainer>
            <VideoPlayer 
              controls 
              preload="metadata"
              poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='400' viewBox='0 0 600 400'%3E%3Crect width='600' height='400' fill='%23f7fafc'/%3E%3Ctext x='300' y='200' text-anchor='middle' fill='%23e53e3e' font-size='24' font-family='Arial'%3E🎥 Sophia's Interview%3C/text%3E%3C/svg%3E"
            >
              <source src="https://maggies-miracle-media-2025-ssoward.s3.amazonaws.com/video/2025-06-28/sophias-thougths-maggies-event.mov" type="video/mp4" />
              <source src="https://maggies-miracle-media-2025-ssoward.s3.amazonaws.com/video/2025-06-28/sophias-thougths-maggies-event.mov" type="video/quicktime" />
              Your browser does not support the video tag.
            </VideoPlayer>
          </VideoContainer>
        </KidsStoryContent>
      )
    }
  }), []);

  const photoCategories = useMemo(() => [
    {
      title: "Hospital Journey",
      description: "Maggie's time in the ICU, medical care, and precious family moments during recovery",
      photos: [
        // Photos sorted chronologically (oldest first) based on actual creation dates
        // November 28, 2024 - Initial hospital stay
        { filename: "IMG_7707.jpeg", caption: "Family gathering during recovery" },
        { filename: "IMG_7708.jpeg", caption: "Hospital room moments" },
        { filename: "IMG_7715.jpeg", caption: "Medical team care" },
        { filename: "IMG_1140.jpeg", caption: "Recovery milestone" },
        // { filename: "IMG_7720.jpeg", caption: "Recovery progress" },
        { filename: "IMG_7201.jpeg", caption: "Family together" },
        
        // November 30, 2024 - Critical care period
        // { filename: "IMG_1139.jpeg", caption: "Hospital care" },
        { filename: "IMG_7722.jpeg", caption: "Family support" },
        // { filename: "IMG_1138.jpeg", caption: "Family support" },
        { filename: "IMG_7723.jpeg", caption: "Healing moments" },
        { filename: "IMG_7724.jpeg", caption: "Hospital journey" },
        // { filename: "IMG_2679.jpeg", caption: "Medical team dedication" },
        { filename: "IMG_2680.jpeg", caption: "Recovery support" },
        { filename: "IMG_2682.jpeg", caption: "Family presence" },
        // { filename: "IMG_7725.jpeg", caption: "Medical monitoring" },
        { filename: "IMG_2684.jpeg", caption: "ICU recovery" },
        { filename: "IMG_2522.jpeg", caption: "Hospital care and support" },
        // { filename: "IMG_2685.jpeg", caption: "Hospital journey" },
        { filename: "IMG_2686.jpeg", caption: "Family support" },
        { filename: "IMG_2687.jpeg", caption: "Recovery moments" },
        { filename: "IMG_7729.jpeg", caption: "Recovery support" },
        
        // December 2, 2024 - Critical recovery moments
        { filename: "IMG_0997.jpeg", caption: "First moments after the miracle" },
        { filename: "IMG_0998.jpeg", caption: "Family gathering at bedside" },
        { filename: "IMG_0999.jpeg", caption: "Hospital support team" },
        { filename: "IMG_1008.jpeg", caption: "Recovery progress" },
        { filename: "IMG_7733.jpeg", caption: "Recovery milestone" },
        // { filename: "IMG_7734.jpeg", caption: "Family together" },
        
        // December 2, 2024 - Special visit
        { filename: "KirstenHall-and-HeatherJordan.jpeg", caption: "Kirsten Hall and Heather Jordan visiting" },
        
        // December 3-5, 2024 - Continued recovery
        { filename: "IMG_7736.jpeg", caption: "Medical progress" },
        { filename: "IMG_7743.jpeg", caption: "Medical progress" },
        { filename: "IMG_1036.jpeg", caption: "Medical monitoring" },
        // { filename: "IMG_1142.jpeg", caption: "Family together" },
        { filename: "IMG_7745.jpeg", caption: "Hospital stay" },
        { filename: "IMG_7746.jpeg", caption: "Family support" },
        { filename: "IMG_7747.jpeg", caption: "Recovery time" },
        { filename: "IMG_7748.jpeg", caption: "Hospital journey" },
        // { filename: "1E234C9A-AF73-4241-B942-166F7694F7C3.jpeg", caption: "Hospital moments with family" },
        // { filename: "IMG_1144.jpeg", caption: "Healing progress" },
        { filename: "IMG_7749.jpeg", caption: "Hospital moments" },
        { filename: "IMG_7750.jpeg", caption: "Healing journey" },
        { filename: "IMG_7751.jpeg", caption: "Recovery progress" },
        { filename: "D8F46627-3BA3-4615-99C3-A11D0197BCB2.jpeg", caption: "Medical care and monitoring" },
        
        // December 8, 2024 - Continued progress
        { filename: "IMG_7756.jpeg", caption: "Family together" },
        { filename: "IMG_7757.jpeg", caption: "Hospital milestone" },
        
        // December 12-14, 2024 - Recovery celebration
        { filename: "IMG_7773.jpeg", caption: "Recovery celebration" },
        { filename: "IMG_7779.jpeg", caption: "Hospital journey completion" },
        
        // June 28, 2025 - Recent reflection
        { filename: "IMG_9204.jpeg", caption: "Final recovery moments" }
      ]
    },
    // {
    //   title: "Emergency Response",
    //   description: "The heroes who responded to save Maggie's life",
    //   icon: "🚑",
    //   photos: [
    //     { filename: "Marg.png", caption: "Emergency response and care" }
    //   ]
    // },
    {
      title: "Kids' Stories",
      description: "How the Soward children experienced their mom's miracle - stories, interviews, and perspectives",
      photos: [],
      isKidsSection: true
    }
  ], []);

  const openModal = (photo, category) => {
    setSelectedPhoto({ ...photo, category });
  };

  const closeModal = () => {
    setSelectedPhoto(null);
  };

  // Get all photos in order for navigation
  // Initialize image preloader for better performance
  const getAllPhotos = useCallback(() => {
    const allPhotos = [];
    photoCategories.forEach(category => {
      if (category.photos && category.photos.length > 0) {
        category.photos.forEach(photo => {
          allPhotos.push({ ...photo, category });
        });
      }
    });
    return allPhotos;
  }, [photoCategories]);

  // Initialize intelligent image preloading
  const allPhotos = getAllPhotos();
  const currentPhotoIndex = selectedPhoto ?
    allPhotos.findIndex(photo =>
      photo.filename === selectedPhoto.filename &&
      photo.category.title === selectedPhoto.category.title
    ) : -1;

  const { preloadOnHover } = useImagePreloader({
    images: allPhotos,
    currentIndex: currentPhotoIndex,
    preloadCount: 3,
    enabled: true
  });

  const navigatePhoto = useCallback((direction) => {
    if (!selectedPhoto) return;
    
    const allPhotos = getAllPhotos();
    const currentIndex = allPhotos.findIndex(photo => 
      photo.filename === selectedPhoto.filename && 
      photo.category.title === selectedPhoto.category.title
    );
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentIndex + 1) % allPhotos.length;
    } else {
      newIndex = currentIndex - 1 < 0 ? allPhotos.length - 1 : currentIndex - 1;
    }
    
    setSelectedPhoto(allPhotos[newIndex]);
  }, [selectedPhoto, getAllPhotos]);

  // Keyboard event handler
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!selectedPhoto) return;
      
      switch (event.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          navigatePhoto('prev');
          break;
        case 'ArrowRight':
          event.preventDefault();
          navigatePhoto('next');
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [selectedPhoto, navigatePhoto]);

  return (
    <GalleryContainer>
      <SEO
        title="Heroes & Journey"
        description="Meet the emergency responders who saved Maggie's life and see the journey that followed. Individual hero recognition, hospital recovery photos, and the complete story of this Thanksgiving miracle."
        url="/gallery"
        image="/uploads/EMTS_AT_WORK.jpg"
        type="website"
      />
      <PageHeader>
        <PageTitle>🚨 Heroes & Journey</PageTitle>
        <PageSubtitle>
          Honoring the emergency responders who made the miracle possible and following the recovery journey that continues to inspire
        </PageSubtitle>
      </PageHeader>

      {/* Hero Recognition Section */}
      <CategorySection
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <CategoryTitle>Our Life-Saving Heroes</CategoryTitle>
        <CategoryDescription>
          These extraordinary emergency responders administered 26 minutes of life-saving efforts,
          demonstrating unwavering dedication and professional excellence that made the miracle possible.
        </CategoryDescription>

        <HeroRecognitionGrid>
          <HeroCard>
            <HeroImage src={crewImages['Ryan Rackman']} alt="Captain Ryan Rackman" />
            <HeroName>Captain Ryan Rackman</HeroName>
            <HeroRole>Fire Captain</HeroRole>
          </HeroCard>

          <HeroCard>
            <HeroImage src={crewImages['Alec M. Olsen']} alt="Firefighter/AEMT Alec M. Olsen" />
            <HeroName>Firefighter/AEMT Alec Olsen</HeroName>
            <HeroRole>Advanced EMT</HeroRole>
          </HeroCard>

          <HeroCard>
            <HeroImage src={crewImages['Ivan Radcliff']} alt="Engineer Ivan Radcliff" />
            <HeroName>Engineer Ivan Radcliff</HeroName>
            <HeroRole>Fire Engineer</HeroRole>
          </HeroCard>

          <HeroCard>
            <HeroImage src={crewImages['Isaac Ostler']} alt="Critical Care Paramedic Isaac Ostler" />
            <HeroName>Critical Care Paramedic Isaac Ostler</HeroName>
            <HeroRole>Critical Care Paramedic</HeroRole>
          </HeroCard>

          <HeroCard>
            <HeroImage src={crewImages['Eliza Soward']} alt="Eliza Soward" />
            <HeroName>Eliza Soward</HeroName>
            <HeroRole>Family Hero</HeroRole>
          </HeroCard>

          <HeroCard>
            <HeroImage src={crewImages['Josh Cluff']} alt="Firefighter/EMT Josh Cluff" />
            <HeroName>Firefighter/EMT Josh Cluff</HeroName>
            <HeroRole>Emergency Medical Technician</HeroRole>
          </HeroCard>

          <HeroCard>
            <HeroImage src={crewImages['Seis Wetherell']} alt="Captain Seis Wetherell" />
            <HeroName>Captain Seis Wetherell</HeroName>
            <HeroRole>Fire Captain</HeroRole>
          </HeroCard>

          <HeroCard>
            <HeroImage src={crewImages['David Perry']} alt="Engineer David Perry" />
            <HeroName>Engineer David Perry</HeroName>
            <HeroRole>Fire Engineer</HeroRole>
          </HeroCard>

          <HeroCard>
            <HeroImage src={crewImages['Abby Walz']} alt="Paramedic Abby Walz" />
            <HeroName>Paramedic Abby Walz</HeroName>
            <HeroRole>Lead Paramedic</HeroRole>
          </HeroCard>
        </HeroRecognitionGrid>

        <CityRecognitionBanner>
          <CityIcon>🏛️</CityIcon>
          <CityRecognitionText>
            <CityRecognitionTitle>
              <a href="/elizas-award" style={{ color: 'white', textDecoration: 'none' }}>
                Official City Recognition →
              </a>
            </CityRecognitionTitle>
            <CityRecognitionDescription>
              Award ceremony celebration hosted by Saratoga Springs City Council
              with Mayor Jim Miller, honoring these heroes for their exceptional service
            </CityRecognitionDescription>
          </CityRecognitionText>
        </CityRecognitionBanner>
      </CategorySection>

      {photoCategories.map((category, categoryIndex) => (
        <CategorySection
          key={categoryIndex}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
          viewport={{ once: true }}
        >
          <CategoryTitle>{category.title}</CategoryTitle>
          <CategoryDescription>{category.description}</CategoryDescription>
          
          {category.photos.length > 0 ? (
            <PhotoGrid>
              {category.photos.map((photo, photoIndex) => (
                <PhotoItem
                  key={photoIndex}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: photoIndex * 0.02 }}
                  viewport={{ once: true, margin: "50px" }}
                  onClick={() => openModal(photo, category)}
                  onMouseEnter={() => preloadOnHover(photoIndex)}
                >
                  <OptimizedImage
                    category={category.title}
                    filename={photo.filename}
                    alt={photo.caption}
                    thumbnail={true}
                    height="250px"
                    objectFit="cover"
                  />
                  <PhotoOverlay>
                    <PhotoCaption>{photo.caption}</PhotoCaption>
                  </PhotoOverlay>
                </PhotoItem>
              ))}
            </PhotoGrid>
          ) : category.isKidsSection ? (
            <KidsSection>
              <DropdownContainer>
                <DropdownSelect value={selectedKid} onChange={handleKidSelection}>
                  <option value="">Choose a child's story to read...</option>
                  <option value="eliza">Eliza Jane - The Hero (17 years old)</option>
                  <option value="anna">Anna - Gaming Buddy (12 years old) 🎥 Interview</option>
                  <option value="violet">Violet - Little Sister's View (9 years old) 🎥 Interview</option>
                  <option value="sophia">Sophia - The Youngest Helper (6 years old) 🎥 Interview</option>
                </DropdownSelect>
              </DropdownContainer>

              <AnimatePresence mode="wait">
                {selectedKid && kidsStories[selectedKid] && (
                  <motion.div
                    key={selectedKid}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div style={{background: 'linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem'}}>
                      <h2 style={{color: '#2f855a', marginBottom: '0.5rem'}}>{kidsStories[selectedKid].name}</h2>
                      {kidsStories[selectedKid].age && (
                        <p style={{color: '#2f855a', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 'bold'}}>
                          {kidsStories[selectedKid].age}
                        </p>
                      )}
                      <h3 style={{color: '#38a169', marginBottom: '1rem', fontWeight: 'normal'}}>{kidsStories[selectedKid].title}</h3>
                    </div>
                    
                    {kidsStories[selectedKid].content}
                  </motion.div>
                )}
              </AnimatePresence>

              {!selectedKid && (
                <div style={{
                  background: 'linear-gradient(135deg, #e6fffa 0%, #b2f5ea 100%)',
                  padding: '2rem',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <h4 style={{color: '#2c7a7b', marginBottom: '1rem'}}>Stories Available:</h4>
                  <p style={{color: '#2c7a7b'}}>
                    <strong>Eliza Jane (17)</strong> - The hero who performed CPR<br/>
                    <strong>Anna (12)</strong> - Gaming buddy's interview with Dad<br/>
                    <strong>Violet (9)</strong> - Little sister's perspective interview<br/>
                    <strong>Sophia (6)</strong> - The youngest helper's interview<br/>
                    More stories coming soon from Declan (22), Isabel (22), Isaac (21), and Noah (18)...
                  </p>
                </div>
              )}
            </KidsSection>
          ) : (
            <EmptyCategory>
              <h3 style={{ color: '#4a5568', marginBottom: '1rem' }}>Photos Coming Soon</h3>
              <p style={{ color: '#718096' }}>
                We're currently organizing photos for this category. Check back soon!
              </p>
            </EmptyCategory>
          )}
        </CategorySection>
      ))}

      <NextSectionContainer>
        {/* <NextSectionText>
          Continue exploring the timeline of this miraculous day...
        </NextSectionText> */}
        <NextSectionButton href="/timeline">
          ⏰ View Timeline →
        </NextSectionButton>
      </NextSectionContainer>

      <AnimatePresence>
        {selectedPhoto && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <ModalContent
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseButton onClick={closeModal}>×</CloseButton>
              <OptimizedImage
                category={selectedPhoto.category.title}
                filename={selectedPhoto.filename}
                alt={selectedPhoto.caption}
                thumbnail={false}
                preload={true}
                style={{ maxHeight: '80vh', width: '100%', objectFit: 'contain' }}
              />
              <ModalCaption>
                <h3 style={{ color: '#2c5282', marginBottom: '0.5rem' }}>
                  {selectedPhoto.category.title}
                </h3>
                <p style={{ color: '#4a5568', margin: '0 0 1rem 0' }}>
                  {selectedPhoto.caption}
                </p>
                <DownloadButton
                  category={selectedPhoto.category.title}
                  filename={selectedPhoto.filename}
                  caption={selectedPhoto.caption}
                  onError={(error) => console.error('Download error:', error)}
                  style={{ marginTop: '1rem' }}
                />
              </ModalCaption>

              <NavigationButton className="prev" onClick={() => navigatePhoto('prev')}>
                ◀
              </NavigationButton>
              <NavigationButton className="next" onClick={() => navigatePhoto('next')}>
                ▶
              </NavigationButton>

              <KeyboardHint>
                Use ← → arrows to navigate
              </KeyboardHint>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>
    </GalleryContainer>
  );
};

export default Gallery;
